import { Form } from './form';
import { Config } from '../config';
import { Order } from '../config/order';
import { BillingAddress } from '../config/billing-address';
import { TaxProvider } from '../providers/tax-provider';
import { TaxView } from '../view/tax-view';
import { TaxValidator } from '../validators/tax-validator';
import { Util } from '../config/utils';

export class TaxForm extends Form {

    private static IS_RESPONSE_PENDING = false;

    countryFieldName = 'tax-billing-countries';
    stateFieldName = 'tax-billing-states';
    zipFieldName = 'tax-billing-zip';

    provider: TaxProvider = new TaxProvider();
    view: TaxView = new TaxView();
    private providerConfig: any;
    private addressConfig: any;
    
    async init(config: Config, callbacks: any) {
        await super.init(config, callbacks);
        this.validators.push(new TaxValidator(this.isResponsePending));
        this.providerConfig = this.getProviderConfig();
        await this.provider.getAddressConfig(this.providerConfig)
            .then(response => {
                this.addressConfig = response;
            });
        return Promise.resolve();
    }

    getProviderConfig() {
        return {
            host: process.env.API_URL,
            portalId: this.config.user.portalId
        };
    }

    async render() {
        this.view.render(this.addressConfig);

        this.hideStatesElement();
        this.hideZipElement();
        this.hideZipError();

        this.attachTaxCalc(`[name=${this.countryFieldName}]`);
        this.attachTaxCalc(`[name=${this.stateFieldName}]`);
        this.attachTaxCalc(`[name=${this.zipFieldName}]`);
        this.preselectCountry();

        return Promise.resolve();
    }

    attachTaxCalc(selector: string) {
        const htmlElement = this.view.getFromHSFrame(selector) ||
            this.view.getHtmlElement(selector);

        if (htmlElement) {
            htmlElement.addEventListener('change', (event) => {
                this.handleTaxElementChange(selector);                
            });
        }
    }

    handleTaxElementChange = (selector: string) => {
        const billingAddress = new BillingAddress();

        const country = this.getElementValue(`[name=${this.countryFieldName}]`);
        if (selector === `[name=${this.countryFieldName}]` && country) {
            this.handleCountryChange(country);
        }
        if (country) {
            billingAddress.country = country;
        }

        const state = this.getElementValue(`[name=${this.stateFieldName}]`);
        if (state) {
            billingAddress.state = state;
        }

        const zip = this.getElementValue(`[name=${this.zipFieldName}]`);
        if (zip) {
            billingAddress.zip = zip;
        }

        TaxForm.IS_RESPONSE_PENDING = true;

        this.provider.getRate(this.providerConfig, billingAddress).then(result => {
            if (result) {
                this.config.settings.tax = result.rate;
                this.triggerEvent('amountChange');
                TaxForm.IS_RESPONSE_PENDING = false;
            }
        });
    }

    isResponsePending = (): boolean => {
        return TaxForm.IS_RESPONSE_PENDING;
    }

    handleCountryChange = (country: string) => {
        this.clearStateOptions();
        this.hideZipError();

        switch (country) {
            case 'US':
                this.populateStateOptions(this.addressConfig.states['US']);
                this.showStatesElement();
                this.showZipElement();
                break;
            case 'CA':
                this.populateStateOptions(this.addressConfig.states['CA']);
                this.showStatesElement();
                this.clearZipElement();
                this.hideZipElement();
                break;
            default:
                this.hideStatesElement();
                this.clearZipElement();
                this.hideZipElement();
                break;
        }
    }

    getElementValue(selector: string) {
        const htmlElement = (this.view.getFromHSFrame(selector) ||
        this.view.getHtmlElement(selector)) as HTMLInputElement;
        return htmlElement ? htmlElement.value : null;
    }

    setElementValue(selector: string, value: string){
        const htmlElement = (this.view.getFromHSFrame(selector) ||
            this.view.getHtmlElement(selector)) as HTMLInputElement;

        if (htmlElement) {
            htmlElement.value = value;
            htmlElement.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }

    showStatesElement = () => {
        const element = this.view.getHtmlElement(`#${this.stateFieldName}-container`);
        this.view.show(element);
    }

    hideStatesElement = () => {
        const element = this.view.getHtmlElement(`#${this.stateFieldName}-container`);
        this.view.hide(element);
    }

    showZipElement = () => {
        const element = this.view.getHtmlElement(`#${this.zipFieldName}-container`);
        this.view.show(element);
    }

    hideZipElement = () => {
        const containerElement = this.view.getHtmlElement(`#${this.zipFieldName}-container`);
        this.view.hide(containerElement);
    }

    clearZipElement = () => {
        const inputElement = this.view.getHtmlElement(`#${this.zipFieldName}`) as HTMLInputElement;
        inputElement.value = '';
    }

    hideZipError = () => {
        this.view.hideZipError();
    }

    clearStateOptions = () => {
        this.view.clearStateOptions();
    }

    populateStateOptions = (states: any[]) => {
        this.view.populateStateOptions(states);
    }

    preselectCountry() {
        Util.getCountryCode().then((res: any) => {
            if (res && res.country_code) {
                this.setElementValue(`[name=${this.countryFieldName}]`, res.country_code);
            }
        });
     
    }

    submit(order: Order) {
        return Promise.resolve(order);
    }

}
